// New-user SSO completion page.
//
// The backend /auth/sso/{provider}/callback redirects here when it sees an
// email that has no existing user record. We collect the workspace name,
// POST /auth/sso/complete-signup, and then jump to /dashboard.
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Buildings, GoogleLogo, MicrosoftOutlookLogo, CircleNotch, ArrowRight } from '@phosphor-icons/react';

export default function SSOCompletePage() {
  const navigate = useNavigate();
  const { API, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const provider = searchParams.get('provider');
  const email = searchParams.get('email') || '';
  const name = searchParams.get('name') || '';
  const ssoId = searchParams.get('ssoId') || '';
  const picture = searchParams.get('picture') || '';

  const [orgName, setOrgName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const providerLabel = provider === 'microsoft' ? 'Microsoft' : 'Google';
  const Icon = provider === 'microsoft' ? MicrosoftOutlookLogo : GoogleLogo;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) { setError('Workspace name is required'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/sso/complete-signup`, {
        email, name, provider, ssoId, picture, organization_name: orgName.trim(),
      }, { withCredentials: true });
      if (checkAuth) await checkAuth();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete sign-up');
      setSubmitting(false);
    }
  };

  // If the backend dropped us here without required params, send the user
  // back to /login so they can restart the SSO flow cleanly.
  if (!provider || !email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white text-sm">
        Missing SSO context. <button onClick={() => navigate('/login')} className="ml-2 underline">Back to login</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4" data-testid="sso-complete-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex items-center gap-3 mb-4">
          {picture ? (
            <img src={picture} alt={name} className="w-12 h-12 rounded-full ring-2 ring-slate-100" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center">
              <Icon size={24} weight="bold" className="text-indigo-600" />
            </div>
          )}
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">Signed in with {providerLabel}</p>
            <p className="text-[14px] font-semibold text-slate-900">{name || email}</p>
            <p className="text-[12px] text-slate-500">{email}</p>
          </div>
        </div>
        <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900 mt-2">One last step</h1>
        <p className="text-sm text-slate-600 mt-1">Name your workspace — you can invite teammates right after.</p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sso-org" className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">Workspace / Company name</Label>
            <div className="relative">
              <Buildings className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
              <Input
                id="sso-org"
                data-testid="sso-org-input"
                type="text"
                placeholder="Acme Inc."
                className="pl-10 h-11 border-zinc-200"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                required
                autoFocus
              />
            </div>
          </div>
          {error && <p className="text-sm text-red-600" data-testid="sso-complete-error">{error}</p>}
          <Button
            type="submit"
            disabled={submitting}
            data-testid="sso-complete-submit"
            className="w-full h-11 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium"
          >
            {submitting ? <CircleNotch className="animate-spin" size={16} /> : <>Create workspace <ArrowRight className="ml-2" size={16} /></>}
          </Button>
        </form>
      </div>
    </div>
  );
}
